import React, { useRef, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { useGetProductsEnAvantQuery } from "../../utils/api/product/product.api";
import { MeilleursProduitsCard } from "./MeilleursProduitsCard";

const AUTO_SCROLL_INTERVAL_MS = 4500;

function MeilleursProduitsSkeleton() {
  return (
    <div className="meilleurs-produits-scroll">
      {Array(5)
        .fill(0)
        .map((_, i) => (
          <div key={i} className="meilleurs-produits-scroll-item">
            <div className="meilleurs-produits-card meilleurs-produits-card-skeleton">
              <div className="meilleurs-produits-card-inner">
                <div className="meilleurs-produits-card-image-wrapper">
                  <Skeleton height="100%" style={{ minHeight: 200 }} />
                </div>
                <div className="meilleurs-produits-card-content">
                  <Skeleton width="80%" height={22} className="mb-2" />
                  <Skeleton width="50%" height={14} className="mb-2" />
                  <Skeleton count={2} height={12} className="mb-2" />
                  <Skeleton width="40%" height={20} className="mb-3" />
                  <Skeleton height={44} className="mb-2" />
                  <Skeleton height={44} />
                </div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

const BANNER_PAGE = 1;
const BANNER_LIMIT = 10;

/**
 * Bannière d'accueil : bandeau pleine largeur, sans titre.
 * Cartes produits scrollables horizontalement ; chaque carte = 100% hauteur bannière × 60% largeur.
 * Design : image à gauche (~40%), détails à droite (~60%), typo et boutons selon maquette.
 */
function BannerHomepage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: produitsEnAvant, isLoading } = useGetProductsEnAvantQuery({
    page: BANNER_PAGE,
    limit: BANNER_LIMIT,
  });

  const results = produitsEnAvant?.results ?? [];

  useEffect(() => {
    if (isLoading || results.length <= 1) return;
    const el = scrollRef.current;
    if (!el) return;

    const step = () => {
      if (!el) return;
          const item = el.querySelector<HTMLElement>(".meilleurs-produits-scroll-item");
          const itemWidth = item?.offsetWidth ?? el.clientWidth;
          const maxScroll = el.scrollWidth - el.clientWidth;
          if (maxScroll <= 0) return;
          const next = el.scrollLeft + itemWidth;
          if (next >= maxScroll - 2) {
            el.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            el.scrollBy({ left: itemWidth, behavior: "smooth" });
          }
        };

    const id = setInterval(step, AUTO_SCROLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoading, results.length]);

  return (
    <section
      className="section-meilleurs-produits-banner"
      aria-label="Produits mis en avant"
    >
      <div className="meilleurs-produits-scroll-wrapper">
        {isLoading ? (
          <MeilleursProduitsSkeleton />
        ) : (
          <div ref={scrollRef} className="meilleurs-produits-scroll" role="list">
            {results.slice(0, 10).map((item, key) => (
              <div
                key={item?.id ?? key}
                className="meilleurs-produits-scroll-item"
                role="listitem"
              >
                <MeilleursProduitsCard item={item} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default BannerHomepage;
